/**
 * Iowa State-Specific Diagnostic Questions
 * 
 * CRITICAL REGULATORY FRAMEWORK:
 * ==============================
 * Iowa does NOT have state-level home health agency licensure.
 * 
 * Home Health Agencies (Medicare-certified):
 * - Certified under federal 42 CFR 484 (Medicare Conditions of Participation)
 * - DIAL serves as state survey agency for Medicare certification
 * - No separate Iowa state license exists
 * 
 * Non-Medical Home Care Agencies:
 * - NO state license requirement
 * - Iowa Health Care Association offers VOLUNTARY accreditation
 * - Must still comply with background check and abuse reporting laws
 * 
 * Key Iowa Laws:
 * - Iowa Code 135C.33: Background checks via SING
 * - Iowa Code 235B: Dependent Adult Abuse
 * - Iowa Code 235A: Child Abuse
 * - 441 IAC 78.9: Medicaid home health services
 * 
 * Regulatory Bodies:
 * - DIAL (Department of Inspections, Appeals, & Licensing): State survey agency
 * - Iowa HHS: Medicaid enrollment, abuse registries
 * - DCI (Division of Criminal Investigation): Criminal history via SING
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const IOWA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: AGENCY TYPE & CERTIFICATION
  // ============================================
  {
    id: 'IA-A1',
    itemId: 'A1',
    stateCode: 'IA',
    title: 'Agency Type Determination',
    description: 'Iowa distinguishes between Medicare-certified home health agencies and non-medical home care providers. There is NO state license for either type.',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Agency Type',
    agencyName: 'Department of Inspections, Appeals, & Licensing',
    agencyAbbr: 'DIAL',
    agencyPhone: '(515) 281-7102',
    agencyUrl: 'https://dial.iowa.gov/licenses/health/facilities',
    questions: [
      {
        id: 'q1',
        question: 'What type of home care services does your agency provide?',
        inputType: 'select',
        options: [
          { value: 'medicare_hha', label: 'Skilled nursing and therapy services (Medicare Home Health Agency)' },
          { value: 'nonmedical', label: 'Non-medical personal care, homemaker, companion services only' },
          { value: 'both', label: 'Both skilled and non-medical services' },
        ],
        required: true,
        helpText: 'Iowa does NOT have state licensure for home care agencies. Medicare-certified HHAs are regulated federally under 42 CFR 484.',
      },
    ],
    scoringRules: [
      { condition: 'exists', questionId: 'q1', result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency type has been identified. Note: Iowa does not require state licensure for home care agencies.',
      atRisk: 'Clarify your service types to determine applicable federal and state requirements.',
      nonCompliant: 'Unable to determine agency type.',
      unknown: 'Please identify your agency type to proceed with the assessment.',
    },
    stateNotes: [
      'Iowa does NOT have state licensure for home health agencies',
      'Medicare-certified HHAs must meet 42 CFR 484 Conditions of Participation',
      'Non-medical home care agencies have no state license requirement',
      'Iowa Health Care Association offers voluntary accreditation for non-medical providers',
      'All agencies must comply with background check (135C.33) and abuse reporting (235B) laws',
    ],
  },

  {
    id: 'IA-A2',
    itemId: 'A2',
    stateCode: 'IA',
    title: 'Medicare Certification Status',
    description: 'Medicare-certified home health agencies must meet federal Conditions of Participation under 42 CFR 484',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare Certification',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/medicare/health-safety-standards/certification-compliance/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Medicare-covered skilled home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Non-medical/private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What is your current Medicare certification status?',
        inputType: 'select',
        options: [
          { value: 'certified', label: 'Certified and in good standing' },
          { value: 'conditional', label: 'Certified with conditions/plan of correction' },
          { value: 'pending', label: 'Certification application pending' },
          { value: 'not_certified', label: 'Not Medicare certified' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'When was your last Medicare survey conducted by DIAL?',
        inputType: 'select',
        options: [
          { value: 'within_year', label: 'Within the past year' },
          { value: '1_3_years', label: '1-3 years ago' },
          { value: 'over_3_years', label: 'More than 3 years ago' },
          { value: 'initial', label: 'Awaiting initial survey' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'in',
          value: ['certified', 'conditional'],
        },
        helpText: 'DIAL conducts surveys on behalf of CMS. Standard survey cycle is approximately every 36 months.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'certified', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'conditional', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_certified', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification is current or not required for your services.',
      atRisk: 'Medicare certification status requires attention. Complete any outstanding plan of correction or follow up on pending application.',
      nonCompliant: 'Providing Medicare-covered services without certification violates federal law. Contact DIAL at (515) 281-7102 or CMS.',
      unknown: 'Verify Medicare certification status if providing skilled home health services.',
    },
    stateNotes: [
      'DIAL serves as the state survey agency for Medicare certification',
      'No separate Iowa state license required - Medicare certification IS the authorization',
      'Initial certification requires survey by DIAL before providing Medicare services',
      'Accreditation by CHAP, ACHC, or Joint Commission may substitute for some survey requirements',
    ],
  },

  {
    id: 'IA-A3',
    itemId: 'A3',
    stateCode: 'IA',
    title: 'Iowa Medicaid Provider Enrollment',
    description: 'Iowa Medicaid home health provider enrollment through managed care organizations',
    severity: 'high',
    regulationCode: '441 IAC 78.9',
    regulationName: 'Medicaid Enrollment',
    agencyName: 'Iowa Department of Health and Human Services',
    agencyAbbr: 'Iowa HHS',
    agencyPhone: '(515) 256-4600',
    agencyUrl: 'https://hhs.iowa.gov/ime/providers',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Iowa Medicaid-funded home health or personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Medicare/Private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are you enrolled with Iowa Medicaid?',
        inputType: 'select',
        options: [
          { value: 'enrolled', label: 'Yes, currently enrolled' },
          { value: 'pending', label: 'Enrollment pending' },
          { value: 'not_enrolled', label: 'Not enrolled' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Which Iowa Medicaid MCOs are you contracted with? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'amerigroup', label: 'Amerigroup Iowa' },
          { value: 'iowa_total_care', label: 'Iowa Total Care' },
          { value: 'molina', label: 'Molina Healthcare of Iowa' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'equals',
          value: 'enrolled',
        },
        helpText: 'Iowa uses managed care for most Medicaid home health services. Contract with MCOs to serve their members.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'enrolled', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_enrolled', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Iowa Medicaid provider enrollment is current.',
      atRisk: 'Medicaid enrollment is pending. Monitor status with Iowa HHS.',
      nonCompliant: 'Cannot provide Medicaid services without enrollment. Contact Iowa HHS at (515) 256-4600.',
      unknown: 'Verify Medicaid enrollment status if providing Iowa Medicaid-funded services.',
    },
    stateNotes: [
      'Iowa Medicaid uses managed care organizations (MCOs) for most services',
      'MCOs: Amerigroup Iowa, Iowa Total Care, Molina Healthcare of Iowa',
      'Must contract with individual MCOs after Medicaid enrollment',
      'EVV required for Medicaid personal care and home health services',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND CHECKS - IOWA CODE 135C.33
  // ============================================
  {
    id: 'IA-B1',
    itemId: 'B1',
    stateCode: 'IA',
    title: 'SING Background Check System',
    description: 'Iowa Code 135C.33 requires background checks through SING (Single Contact Repository) for home health employees providing direct services',
    severity: 'high',
    regulationCode: '135C.33',
    regulationName: 'SING Checks',
    agencyName: 'Iowa Division of Criminal Investigation',
    agencyAbbr: 'DCI',
    agencyPhone: '(515) 725-6010',
    agencyUrl: 'https://dps.iowa.gov/divisions/criminal-investigation',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct background checks through SING (Single Contact Repository) for all employees providing direct services?',
        inputType: 'select',
        options: [
          { value: 'all_sing', label: 'Yes, SING checks for all direct service employees' },
          { value: 'some_sing', label: 'SING for some, other methods for others' },
          { value: 'other_only', label: 'Use other background check methods only' },
          { value: 'none', label: 'No background checks conducted' },
        ],
        required: true,
        helpText: 'SING provides access to Iowa Criminal History (DCI), Sex Offender Registry, Child Abuse Registry, and Dependent Adult Abuse Registry in one system.',
      },
      {
        id: 'q2',
        question: 'Which databases do you check through SING? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'criminal', label: 'Iowa Criminal History (DCI)' },
          { value: 'sex_offender', label: 'Iowa Sex Offender Registry' },
          { value: 'child_abuse', label: 'Child Abuse Registry' },
          { value: 'daa', label: 'Dependent Adult Abuse Registry' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'in',
          value: ['all_sing', 'some_sing'],
        },
      },
      {
        id: 'q3',
        question: 'Do you obtain signed waivers from applicants for criminal history checks?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, signed waivers obtained' },
          { value: 'no', label: 'No waivers obtained' },
        ],
        helpText: 'Without a signed waiver, SING will not provide: (1) deferred judgments where probation was completed, or (2) arrest information older than 18 months without court disposition.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all_sing' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some_sing' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['other_only', 'none'], result: 'NON_COMPLIANT' },
      // No signed waivers means incomplete background check info
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check process using SING meets Iowa Code 135C.33 requirements.',
      atRisk: 'Background check process may be incomplete. Iowa Code 135C.33 requires checking all four registries through SING. Without signed waivers, deferred judgments and older arrests may not be revealed.',
      nonCompliant: 'Failure to conduct SING background checks violates Iowa Code 135C.33. Contact DCI at (515) 725-6010 to establish SING access.',
      unknown: 'Verify SING background check procedures are in place per Iowa Code 135C.33.',
    },
    stateNotes: [
      'SING = Single Contact Repository - provides one-stop access to multiple registries',
      'Request SING access by emailing dciaccounts@dps.state.ia.us',
      'Fee per request is deducted from pre-paid account or credit card',
      'Signed waiver required to receive complete criminal history information',
      'Iowa Code 135C.33 applies to home health agency employees providing direct services',
    ],
  },

  {
    id: 'IA-B2',
    itemId: 'B2',
    stateCode: 'IA',
    title: 'Provisional Employment Pending Background Check',
    description: 'Iowa allows provisional employment under specific conditions while SING background check is pending',
    severity: 'high',
    regulationCode: '135C.33',
    regulationName: 'Provisional Employment',
    agencyName: 'Department of Inspections, Appeals, & Licensing',
    agencyAbbr: 'DIAL',
    agencyPhone: '(515) 281-7102',
    agencyUrl: 'https://dial.iowa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you ever allow employees to begin work before SING background check results are received?',
        inputType: 'select',
        options: [
          { value: 'never', label: 'Never - all employees cleared through SING before any work' },
          { value: 'provisional', label: 'Yes, provisional employment with compliant preliminary check' },
          { value: 'start_without', label: 'Yes, employees sometimes start without any background check' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'If using provisional employment, do you use an approved third-party vendor for comprehensive preliminary background checks?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, approved third-party vendor' },
          { value: 'no', label: 'No / Not applicable' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'provisional',
        },
        helpText: 'Comprehensive preliminary background check must cover all states where applicant worked or resided in prior 7 years.',
      },
      {
        id: 'q3',
        question: 'Do provisional employees work under direct supervision until SING results are received?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, direct supervision required' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'provisional',
        },
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start without any background check - this is a DIRECT VIOLATION
      { condition: 'equals', questionId: 'q1', value: 'start_without', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if using provisional employment without proper preliminary check
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'provisional' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if using provisional employment without direct supervision
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'provisional' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // COMPLIANT if never start before clearance
      { condition: 'equals', questionId: 'q1', value: 'never', result: 'COMPLIANT' },
      // COMPLIANT if provisional employment with all proper safeguards
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'provisional' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check timing meets Iowa requirements.',
      atRisk: 'Review provisional employment procedures against Iowa requirements.',
      nonCompliant: 'VIOLATION: Employees working before background check clearance is a direct violation of Iowa Code 135C.33. Allowing employees to start without proper preliminary background check from an approved vendor AND direct supervision violates regulations. Immediately remove uncleared staff from client assignments.',
      unknown: 'Review provisional employment procedures against Iowa requirements.',
    },
    stateNotes: [
      'Provisional employment allowed ONLY with comprehensive preliminary background check from approved vendor',
      'Preliminary check must cover all states where applicant worked/resided in prior 7 years',
      'Must not reveal disqualifying felony or crimes under Iowa Code chapters 708-712, 726',
      'SING check must still be submitted within 30 days',
      'Request HHS evaluation if preliminary check reveals ANY crime or abuse finding',
    ],
  },

  {
    id: 'IA-B3',
    itemId: 'B3',
    stateCode: 'IA',
    title: 'Disqualifying Offenses Evaluation',
    description: 'Iowa requires HHS evaluation when background check reveals crimes or abuse findings',
    severity: 'high',
    regulationCode: '135C.33',
    regulationName: 'Disqualifying Offenses',
    agencyName: 'Iowa Department of Health and Human Services',
    agencyAbbr: 'Iowa HHS',
    agencyPhone: '(515) 281-5452',
    agencyUrl: 'https://hhs.iowa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'When a background check reveals a criminal conviction, what is your process?',
        inputType: 'select',
        options: [
          { value: 'hhs_eval', label: 'Request HHS evaluation before employment decision' },
          { value: 'internal_review', label: 'Internal review without HHS evaluation' },
          { value: 'auto_deny', label: 'Automatic denial for any conviction' },
          { value: 'no_process', label: 'No formal process' },
        ],
        required: true,
        helpText: 'Iowa Code 135C.33 requires HHS evaluation to determine if a crime warrants employment prohibition.',
      },
      {
        id: 'q2',
        question: 'When a background check reveals founded child or dependent adult abuse, what is your process?',
        inputType: 'select',
        options: [
          { value: 'hhs_eval', label: 'Request HHS evaluation' },
          { value: 'auto_deny', label: 'Automatic prohibition from employment' },
          { value: 'case_by_case', label: 'Case-by-case internal review' },
          { value: 'no_process', label: 'No formal process' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'hhs_eval' },
        { condition: 'includes', questionId: 'q2', value: ['hhs_eval', 'auto_deny'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'internal_review' },
        { condition: 'equals', questionId: 'q2', value: 'case_by_case' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no_process' },
        { condition: 'equals', questionId: 'q2', value: 'no_process' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Disqualifying offense evaluation process meets Iowa requirements.',
      atRisk: 'Internal review processes may not meet Iowa requirements. HHS evaluation is required before employing anyone with criminal history or abuse findings.',
      nonCompliant: 'No formal process for handling background check findings. Iowa Code 135C.33 requires HHS evaluation.',
      unknown: 'Review disqualifying offense procedures against Iowa Code 135C.33.',
    },
    stateNotes: [
      'HHS evaluation required before employing anyone with criminal conviction',
      'HHS evaluation required for anyone with founded child or dependent adult abuse',
      'Person CANNOT be employed until HHS evaluation is complete (except provisional with approved preliminary check)',
      'Failure to report known conviction or abuse finding is a serious misdemeanor per Iowa Code 135C.33',
    ],
  },

  // ============================================
  // SECTION C: MANDATORY REPORTER TRAINING - IOWA CODE 235B
  // ============================================
  {
    id: 'IA-C1',
    itemId: 'C1',
    stateCode: 'IA',
    title: 'Mandatory Reporter Training - Dependent Adult Abuse',
    description: 'Iowa Code 235B.3(2) designates home health aides as mandatory reporters of dependent adult abuse',
    severity: 'high',
    regulationCode: '235B.16',
    regulationName: 'Mandatory Reporters',
    agencyName: 'Iowa Department of Health and Human Services',
    agencyAbbr: 'Iowa HHS',
    agencyPhone: '(800) 362-2178',
    agencyUrl: 'https://hhs.iowa.gov/report-abuse-fraud/mandatory-reporters',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees providing direct services to dependent adults complete mandatory reporter training?',
        inputType: 'select',
        options: [
          { value: 'all_compliant', label: 'Yes, all complete within 6 months of hire and every 3 years' },
          { value: 'most', label: 'Most employees complete training' },
          { value: 'some', label: 'Some employees complete training' },
          { value: 'none', label: 'No mandatory reporter training provided' },
        ],
        required: true,
        helpText: 'Iowa Code 235B.16 requires 2 hours of training within 6 months of employment and renewal every 3 years.',
      },
      {
        id: 'q2',
        question: 'Which mandatory reporter training do you use?',
        inputType: 'select',
        options: [
          { value: 'hhs_approved', label: 'Iowa HHS-approved curriculum' },
          { value: 'board_approved', label: 'Licensing board approved curriculum' },
          { value: 'employer_developed', label: 'Employer-developed training' },
          { value: 'unknown', label: 'Not sure' },
        ],
        helpText: 'Training must be approved by HHS or the appropriate licensing/examining board.',
      },
      {
        id: 'q3',
        question: 'Do you track mandatory reporter training completion and renewal dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Yes, automated tracking system' },
          { value: 'manual', label: 'Yes, manual tracking' },
          { value: 'no', label: 'No tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all_compliant' },
        { condition: 'includes', questionId: 'q2', value: ['hhs_approved', 'board_approved'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'employer_developed' },
      ], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['some', 'none'], result: 'NON_COMPLIANT' },
      // Not tracking training completion per Iowa Code 235B.16
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Mandatory reporter training program meets Iowa Code 235B requirements.',
      atRisk: 'Mandatory reporter training may be incomplete, use non-approved curriculum, or lack tracking. All home health aides must complete HHS-approved training within 6 months of hire and renew every 3 years.',
      nonCompliant: 'Failure to train employees on mandatory abuse reporting violates Iowa Code 235B. Home health aides are explicitly listed as mandatory reporters. Contact Iowa HHS immediately.',
      unknown: 'Verify mandatory reporter training program meets Iowa Code 235B.16 requirements.',
    },
    stateNotes: [
      'Home health aides ("in-home homemaker-home health aide") explicitly listed in Iowa Code 235B.3(2)',
      '2 hours initial training within 6 months of employment',
      'Renewal every 3 years',
      'Free training available through Iowa HHS Learning Management System',
      'Combined child abuse and dependent adult abuse curriculum available (2 hours)',
      'Must report suspected abuse within 24 hours to Iowa HHS: (800) 362-2178',
    ],
  },

  {
    id: 'IA-C2',
    itemId: 'C2',
    stateCode: 'IA',
    title: 'Abuse Reporting Procedures',
    description: 'Iowa requires oral report of suspected dependent adult abuse within 24 hours',
    severity: 'high',
    regulationCode: '235B.3',
    regulationName: 'Abuse Reporting',
    agencyName: 'Iowa Department of Health and Human Services',
    agencyAbbr: 'Iowa HHS',
    agencyPhone: '(800) 362-2178',
    agencyUrl: 'https://hhs.iowa.gov/report-abuse-fraud',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written procedures for reporting suspected dependent adult abuse?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your procedures include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hotline', label: 'Iowa HHS abuse hotline number: (800) 362-2178' },
          { value: '24_hour', label: '24-hour reporting requirement' },
          { value: 'supervisor', label: 'Immediate notification to supervisor' },
          { value: 'types', label: 'Types of abuse defined (physical, sexual, exploitation, deprivation, personal degradation)' },
          { value: 'immunity', label: 'Immunity protections for good faith reporters' },
          { value: 'penalties', label: 'Penalties for failure to report' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse reporting procedures are comprehensive per Iowa Code 235B.',
      atRisk: 'Abuse reporting procedures may be incomplete. Ensure all staff know to report within 24 hours to (800) 362-2178.',
      nonCompliant: 'No written abuse reporting procedures. This violates Iowa Code 235B. Failure to report is a simple misdemeanor.',
      unknown: 'Review abuse reporting procedures against Iowa Code 235B requirements.',
    },
    stateNotes: [
      'Report to Iowa HHS within 24 hours: (800) 362-2178',
      'Written report no longer required - oral report sufficient',
      'Must also notify supervisor/person in charge immediately',
      'Failure to report is a simple misdemeanor',
      'Knowingly failing to report or interfering with report creates civil liability',
      'Good faith reporters have immunity from civil and criminal liability',
    ],
  },

  // ============================================
  // SECTION D: HOME HEALTH AIDE TRAINING
  // ============================================
  {
    id: 'IA-D1',
    itemId: 'D1',
    stateCode: 'IA',
    title: 'Home Health Aide Training Requirements',
    description: 'Medicare-certified HHAs must meet 42 CFR 484.80 training requirements for home health aides',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'HHA Training',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are you a Medicare-certified home health agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - non-medical services only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How do your home health aides meet federal training requirements?',
        inputType: 'select',
        options: [
          { value: 'hha_program', label: 'Completed HHA training and competency evaluation program (75 hours)' },
          { value: 'cna', label: 'CNA certification (Iowa Direct Care Worker Registry)' },
          { value: 'competency_only', label: 'Competency evaluation only (already trained)' },
          { value: 'none', label: 'No formal training verification' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: '42 CFR 484.80 requires 75 hours training OR competency evaluation OR state-approved CNA program.',
      },
      {
        id: 'q3',
        question: 'Do home health aides complete 12 hours of in-service training annually?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides complete 12+ hours annually' },
          { value: 'most', label: 'Most aides complete required hours' },
          { value: 'no', label: 'No annual in-service requirement enforced' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q2', value: ['hha_program', 'cna', 'competency_only'] },
        { condition: 'equals', questionId: 'q3', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'none' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'default', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Home health aide training meets federal requirements under 42 CFR 484.80.',
      atRisk: 'Some home health aides may not meet training requirements. Verify all aides have 75 hours training or equivalent and complete 12 hours annual in-service.',
      nonCompliant: 'Home health aides not meeting 42 CFR 484.80 training requirements. This is a Medicare Condition of Participation deficiency.',
      unknown: 'Verify home health aide training against 42 CFR 484.80 requirements.',
    },
    stateNotes: [
      'Medicare HHAs: 42 CFR 484.80 requires 75 hours training OR competency evaluation',
      'Iowa CNA certification (75 hours + testing) meets federal HHA training requirement',
      'CNAs listed on Iowa Direct Care Worker Registry',
      '12 hours annual in-service training required for Medicare HHAs',
      'Non-medical agencies have NO state training requirements (but employers typically require First Aid/CPR)',
    ],
  },

  {
    id: 'IA-D2',
    itemId: 'D2',
    stateCode: 'IA',
    title: 'Iowa CNA Registry Verification',
    description: 'Verify CNAs on Iowa Direct Care Worker Registry if employing certified nursing assistants',
    severity: 'medium',
    regulationCode: '42 CFR 483.156',
    regulationName: 'CNA Registry',
    agencyName: 'Department of Inspections, Appeals, & Licensing',
    agencyAbbr: 'DIAL',
    agencyPhone: '(515) 381-7835',
    agencyUrl: 'https://dial.iowa.gov/licenses/health/direct-care-worker-registry',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ CNAs (Certified Nursing Assistants)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you verify CNA status on the Iowa Direct Care Worker Registry before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verify before hire' },
          { value: 'sometimes', label: 'Sometimes verify' },
          { value: 'never', label: 'Do not verify registry status' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Registry shows active status and any findings of abuse, neglect, or misappropriation.',
      },
      {
        id: 'q3',
        question: 'Do you report CNA work hours to the registry?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, we report hours' },
          { value: 'no', label: 'No, we do not report' },
          { value: 'na', label: 'Not applicable - not a qualifying entity' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Home health agencies MAY (but are not required to) report CNA hours to help them maintain active status.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'never', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CNA registry verification process is appropriate.',
      atRisk: 'CNA registry verification is inconsistent. Always verify before hire to check for abuse/neglect findings.',
      nonCompliant: 'Not verifying CNA registry status. Registry shows individuals prohibited from employment due to abuse findings.',
      unknown: 'Verify CNA registry verification procedures.',
    },
    stateNotes: [
      'Iowa Direct Care Worker Registry: https://dia-hfd.iowa.gov/DIA_HFD/Home.do',
      'Registry shows abuse, neglect, mistreatment, or misappropriation findings',
      'Individuals with such findings CANNOT be employed',
      'CNAs must work 8 hours every 24 months to remain active',
      'Home health agencies can report hours to help CNAs maintain active status',
    ],
  },

  // ============================================
  // SECTION E: ELECTRONIC VISIT VERIFICATION (EVV)
  // ============================================
  {
    id: 'IA-E1',
    itemId: 'E1',
    stateCode: 'IA',
    title: 'Electronic Visit Verification (EVV) - Iowa Medicaid',
    description: '21st Century Cures Act requires EVV for Medicaid-funded personal care and home health services',
    severity: 'high',
    regulationCode: 'Cures Act 12006',
    regulationName: 'EVV',
    agencyName: 'Iowa Department of Health and Human Services',
    agencyAbbr: 'Iowa HHS',
    agencyPhone: '(515) 256-4600',
    agencyUrl: 'https://hhs.iowa.gov/medicaid/about-medicaid/medicaid-projects/evv',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Iowa Medicaid-funded personal care or home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Medicare/Private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are you using an EVV system for Medicaid visits?',
        inputType: 'select',
        options: [
          { value: 'carebridge', label: 'Yes, using CareBridge (state aggregator)' },
          { value: 'third_party', label: 'Yes, using approved third-party EVV integrated with CareBridge' },
          { value: 'partial', label: 'EVV used for some visits but not all' },
          { value: 'none', label: 'No EVV system in use' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Iowa uses CareBridge as the state EVV aggregator. Third-party systems must integrate with CareBridge.',
      },
      {
        id: 'q3',
        question: 'Does your EVV system capture all required data points? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'type', label: 'Type of service performed' },
          { value: 'recipient', label: 'Individual receiving service' },
          { value: 'date', label: 'Date of service' },
          { value: 'location', label: 'Location of service delivery' },
          { value: 'provider', label: 'Individual providing service' },
          { value: 'start', label: 'Time service begins' },
          { value: 'end', label: 'Time service ends' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'in',
          value: ['carebridge', 'third_party'],
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q2', value: ['carebridge', 'third_party'] },
        { condition: 'countAtLeast', questionId: 'q3', value: 7 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'none', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for Iowa Medicaid services.',
      atRisk: 'EVV not used for all Medicaid visits. Claims may be denied for services without EVV verification.',
      nonCompliant: 'No EVV system in place for Medicaid services. This violates federal 21st Century Cures Act requirements. Claims will be denied.',
      unknown: 'Verify EVV compliance if providing Iowa Medicaid-funded services.',
    },
    stateNotes: [
      'Iowa EVV effective: Personal care January 1, 2021; Home health January 1, 2023',
      'State aggregator: CareBridge',
      'Open model - providers can use CareBridge or approved third-party systems',
      'Third-party systems must integrate with CareBridge',
      'MCOs: Amerigroup, Iowa Total Care, Molina all require EVV',
      'EVV contact: EVV@hhs.iowa.gov',
      'Assisted Living and Residential Care Facilities may complete attestation to opt out',
    ],
  },

  // ============================================
  // SECTION F: PERSONNEL & DOCUMENTATION
  // ============================================
  {
    id: 'IA-F1',
    itemId: 'F1',
    stateCode: 'IA',
    title: 'Personnel File Documentation',
    description: 'Required documentation for home health employees',
    severity: 'high',
    regulationCode: '484.80',
    regulationName: 'Personnel Records',
    agencyName: 'Department of Inspections, Appeals, & Licensing',
    agencyAbbr: 'DIAL',
    agencyPhone: '(515) 281-7102',
    agencyUrl: 'https://dial.iowa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY direct service employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'application', label: 'Employment application' },
          { value: 'sing', label: 'SING background check results' },
          { value: 'abuse_eval', label: 'HHS evaluation (if background revealed issues)' },
          { value: 'mandatory_reporter', label: 'Mandatory reporter training certificate' },
          { value: 'training', label: 'Training/competency documentation (HHA or CNA)' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'job_description', label: 'Signed job description' },
          { value: 'license', label: 'Professional license verification (if applicable)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation.',
      atRisk: 'Some personnel documentation may be missing. Ensure all files contain SING results and mandatory reporter training.',
      nonCompliant: 'Personnel files are missing critical documentation. SING background checks and mandatory reporter training are required by Iowa law.',
      unknown: 'Audit personnel files for required documentation.',
    },
    stateNotes: [
      'SING background check results required per Iowa Code 135C.33',
      'Mandatory reporter training certificate required per Iowa Code 235B.16',
      'Medicare HHAs must also maintain 42 CFR 484 required documentation',
      'Keep I-9 forms separately from personnel files per USCIS guidance',
    ],
  },

  {
    id: 'IA-F2',
    itemId: 'F2',
    stateCode: 'IA',
    title: 'Record Retention',
    description: 'Record retention requirements for home health agencies',
    severity: 'medium',
    regulationCode: '484.110',
    regulationName: 'Record Retention',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient/client clinical records?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Medicare requires 5 years from discharge per 42 CFR 484.110. Some records may need longer retention.',
      },
      {
        id: 'q2',
        question: 'How long do you retain personnel records after employment ends?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '5plus' },
        { condition: 'equals', questionId: 'q2', value: '5plus' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['3to4'] },
        { condition: 'includes', questionId: 'q2', value: ['3to4'] },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['less3', 'unsure'] },
        { condition: 'includes', questionId: 'q2', value: ['less3', 'unsure'] },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets requirements.',
      atRisk: 'Record retention may not meet Medicare requirements. Clinical records must be retained 5 years from discharge.',
      nonCompliant: 'Record retention does not meet requirements. Implement 5-year retention policy immediately.',
      unknown: 'Review record retention policies against applicable requirements.',
    },
    stateNotes: [
      'Medicare: 42 CFR 484.110 requires 5 years from discharge',
      'Iowa has no separate state retention requirement for HHAs',
      'Medicaid records may need 6+ years for audit purposes',
      'Consider 7 years for all records to cover all contingencies',
    ],
  },

  // ============================================
  // SECTION G: CARE DELIVERY (Medicare HHAs)
  // ============================================
  {
    id: 'IA-G1',
    itemId: 'G1',
    stateCode: 'IA',
    title: 'Plan of Care Requirements',
    description: 'Medicare-certified HHAs must have physician-ordered plan of care per 42 CFR 484.60',
    severity: 'high',
    regulationCode: '484.60',
    regulationName: 'Plan of Care',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are you a Medicare-certified home health agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do all patients have a physician-signed plan of care before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal plan of care process' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Are plans of care reviewed and updated at required intervals?',
        inputType: 'select',
        options: [
          { value: 'every_60', label: 'Yes, every 60 days or more frequently as needed' },
          { value: 'less_frequent', label: 'Less frequently than 60 days' },
          { value: 'no_updates', label: 'No regular update process' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'every_60' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'usually' },
        { condition: 'equals', questionId: 'q3', value: 'less_frequent' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no_updates' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of care process meets Medicare Conditions of Participation.',
      atRisk: 'Plan of care process may not fully meet 42 CFR 484.60 requirements. Ensure physician signature before services and 60-day recertification.',
      nonCompliant: 'Plan of care deficiencies violate Medicare Conditions of Participation. DIAL surveys will cite this.',
      unknown: 'Review plan of care process against 42 CFR 484.60 requirements.',
    },
    stateNotes: [
      '42 CFR 484.60 requires individualized plan of care',
      'Must be signed by physician or allowed practitioner',
      'Review and update every 60 days',
      'Face-to-face encounter required within specific timeframes',
      'OASIS assessment required at specified intervals',
    ],
  },

  {
    id: 'IA-G2',
    itemId: 'G2',
    stateCode: 'IA',
    title: 'QAPI Program',
    description: 'Medicare-certified HHAs must have Quality Assessment and Performance Improvement program',
    severity: 'medium',
    regulationCode: '484.65',
    regulationName: 'QAPI',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are you a Medicare-certified home health agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a documented QAPI program?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, comprehensive QAPI with regular meetings' },
          { value: 'basic', label: 'Yes, basic quality monitoring' },
          { value: 'no', label: 'No QAPI program' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Which QAPI elements do you have? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'data', label: 'Data collection and analysis' },
          { value: 'indicators', label: 'Performance indicators monitored' },
          { value: 'improvement', label: 'Performance improvement projects' },
          { value: 'infection', label: 'Infection control monitoring' },
          { value: 'adverse', label: 'Adverse event tracking' },
          { value: 'governing', label: 'Governing body oversight' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'in',
          value: ['comprehensive', 'basic'],
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'comprehensive' },
        { condition: 'countAtLeast', questionId: 'q3', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'basic', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets Medicare Conditions of Participation.',
      atRisk: 'QAPI program may not be comprehensive enough to meet 42 CFR 484.65 requirements.',
      nonCompliant: 'No QAPI program. This is a Medicare Condition of Participation. DIAL surveys will cite this deficiency.',
      unknown: 'Review QAPI program against 42 CFR 484.65 requirements.',
    },
    stateNotes: [
      '42 CFR 484.65 requires data-driven QAPI program',
      'Must track and analyze quality indicators',
      'Must have performance improvement projects',
      'Governing body must oversee QAPI',
      'Annual program evaluation required',
    ],
  },

  // ============================================
  // SECTION H: HEALTH & SAFETY
  // ============================================
  {
    id: 'IA-H1',
    itemId: 'H1',
    stateCode: 'IA',
    title: 'TB Screening',
    description: 'TB screening requirements for home health employees',
    severity: 'medium',
    regulationCode: '484.70',
    regulationName: 'TB Screening',
    agencyName: 'Iowa Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(515) 281-7689',
    agencyUrl: 'https://hhs.iowa.gov/public-health',
    questions: [
      {
        id: 'q1',
        question: 'Do you require TB screening for employees providing direct patient care?',
        inputType: 'select',
        options: [
          { value: 'pre_hire', label: 'Yes, before patient contact begins' },
          { value: 'after_hire', label: 'Yes, but after starting work' },
          { value: 'no', label: 'No TB screening required' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What method of TB screening do you use?',
        inputType: 'select',
        options: [
          { value: 'tst', label: 'TST (tuberculin skin test)' },
          { value: 'igra', label: 'IGRA (blood test - QuantiFERON or T-SPOT)' },
          { value: 'either', label: 'Either TST or IGRA accepted' },
          { value: 'symptom', label: 'Symptom screening only' },
        ],
      },
    ],
    scoringRules: [
      // 1. NON_COMPLIANT - No TB screening required
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // 2. COMPLIANT - TB screening before patient contact
      { condition: 'equals', questionId: 'q1', value: 'pre_hire', result: 'COMPLIANT' },
      // 3. AT_RISK - TB screening after starting work
      { condition: 'equals', questionId: 'q1', value: 'after_hire', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening program is appropriate.',
      atRisk: 'TB screening should occur before patient contact to protect vulnerable populations.',
      nonCompliant: 'No TB screening program. Medicare HHAs must have infection control policies per 42 CFR 484.70.',
      unknown: 'Review TB screening policies.',
    },
    stateNotes: [
      'CDC recommends baseline TB screening for healthcare workers',
      'Medicare HHAs must have infection control program per 42 CFR 484.70',
      'Iowa follows CDC guidelines for TB screening',
      'Annual testing generally not required unless exposure risk',
    ],
  },

  {
    id: 'IA-H2',
    itemId: 'H2',
    stateCode: 'IA',
    title: 'Emergency Preparedness',
    description: 'Emergency preparedness requirements for Medicare-certified HHAs',
    severity: 'medium',
    regulationCode: '484.102',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are you a Medicare-certified home health agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, comprehensive plan with all required elements' },
          { value: 'partial', label: 'Yes, but may be missing some elements' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Which emergency preparedness elements does your plan address? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'risk', label: 'Risk assessment and planning' },
          { value: 'policies', label: 'Policies and procedures' },
          { value: 'communication', label: 'Communication plan' },
          { value: 'training', label: 'Training and testing' },
          { value: 'patient_tracking', label: 'Patient tracking' },
          { value: 'tornado', label: 'Tornado/severe weather procedures' },
          { value: 'flood', label: 'Flood response' },
          { value: 'winter', label: 'Severe winter weather' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'in',
          value: ['comprehensive', 'partial'],
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'comprehensive' },
        { condition: 'countAtLeast', questionId: 'q3', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan meets requirements.',
      atRisk: 'Emergency preparedness plan may be incomplete. 42 CFR 484.102 requires comprehensive emergency planning.',
      nonCompliant: 'No emergency preparedness plan. This is a Medicare Condition of Participation.',
      unknown: 'Review emergency preparedness against 42 CFR 484.102 requirements.',
    },
    stateNotes: [
      '42 CFR 484.102 requires emergency preparedness for Medicare HHAs',
      'Must address all-hazards approach',
      'Iowa-specific hazards: tornadoes, flooding, severe winter weather, power outages',
      'Annual review and update required',
      'Training and testing (drill or actual emergency response) annually',
    ],
  },

  // ============================================
  // SECTION I: INSURANCE & COMPLIANCE
  // ============================================
  {
    id: 'IA-I1',
    itemId: 'I1',
    stateCode: 'IA',
    title: 'Insurance Coverage',
    description: 'Recommended insurance coverage for home health agencies',
    severity: 'medium',
    regulationCode: 'MCO Requirements',
    regulationName: 'Insurance',
    agencyName: 'Iowa Insurance Division',
    agencyAbbr: 'IID',
    agencyPhone: '(515) 281-5705',
    agencyUrl: 'https://iid.iowa.gov/',
    questions: [
      {
        id: 'q1',
        question: 'What insurance coverage does your agency maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'gl', label: 'General liability insurance ($1M+ per occurrence)' },
          { value: 'prof', label: 'Professional liability/malpractice insurance' },
          { value: 'wc', label: 'Workers\' compensation insurance' },
          { value: 'auto', label: 'Commercial auto insurance (if transporting clients)' },
          { value: 'bond', label: 'Surety/fidelity bond' },
          { value: 'cyber', label: 'Cyber liability insurance' },
        ],
        required: true,
        helpText: 'Iowa does not mandate specific insurance for home care agencies, but MCOs and best practices require adequate coverage.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Insurance coverage is adequate.',
      atRisk: 'Insurance coverage may be insufficient. Most MCOs require general liability, professional liability, and workers\' compensation.',
      nonCompliant: 'Inadequate insurance coverage. This creates significant business risk and may prevent MCO contracting.',
      unknown: 'Review insurance coverage against MCO requirements and industry standards.',
    },
    stateNotes: [
      'Iowa does not mandate specific insurance for non-licensed home care agencies',
      'MCOs typically require: general liability ($1M+), professional liability, workers\' comp',
      'Medicare HHAs should carry professional liability coverage',
      'Workers\' compensation required by Iowa law if you have employees',
      'Surety bond recommended to protect clients from theft',
    ],
  },

  {
    id: 'IA-I2',
    itemId: 'I2',
    stateCode: 'IA',
    title: 'OIG/SAM Exclusion Screening',
    description: 'Federal exclusion screening requirements for Medicare/Medicaid providers',
    severity: 'high',
    regulationCode: '455.436',
    regulationName: 'OIG Screening',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://oig.hhs.gov/exclusions/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicare or Medicaid funded services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you screen all employees against OIG LEIE and SAM.gov exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly screening' },
          { value: 'hire_only', label: 'Yes, at hire only' },
          { value: 'no', label: 'No exclusion screening' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'OIG recommends monthly screening. Employing excluded individuals can result in civil monetary penalties.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Exclusion screening meets federal requirements.',
      atRisk: 'Screening at hire only may not be sufficient. OIG recommends monthly screening to catch new exclusions.',
      nonCompliant: 'No exclusion screening. Employing excluded individuals violates federal law and can result in civil monetary penalties up to $10,000 per item/service.',
      unknown: 'Verify exclusion screening procedures for Medicare/Medicaid compliance.',
    },
    stateNotes: [
      'OIG LEIE: https://exclusions.oig.hhs.gov/',
      'SAM.gov: https://sam.gov/',
      'Screen all employees, contractors, and vendors',
      'Monthly screening recommended',
      'Penalties: up to $10,000 per item/service, treble damages, program exclusion',
    ],
  },
];

export default IOWA_QUESTIONS;